import { useEffect, useState } from "react";
import apiClient from "../utils/apiClient";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { ContactMail } from "@mui/icons-material";
import PageLayout from "../components/admin/PageLayout";
const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
];

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("contact");
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to load contacts", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      const response = await apiClient.put(`contact/${contactId}/status`, {
        status: newStatus,
      });
      if (response.data.success) {
        setContacts((prev) =>
          prev.map((c) => (c.id === contactId ? { ...c, status: newStatus } : c))
        );
        setSnackbar({ open: true, message: "Status updated", severity: "success" });
      } else {
        setSnackbar({ open: true, message: response.data.message || "Update failed", severity: "error" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update status", severity: "error" });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "primary";
      case "in_progress":
        return "warning";
      case "completed":
        return "success";
      default:
        return "default";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "–";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <PageLayout
      title="Contacts"
      subtitle="Messages from the Contact Us form. Update status as you handle each enquiry."
    >
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <CircularProgress size={48} sx={{ color: "primary.main" }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Message</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <ContactMail sx={{ fontSize: 48, color: "action.disabled", display: "block", mx: "auto", mb: 1 }} />
                    <Typography color="text.secondary">No contact submissions yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(row.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={500}>{row.name}</Typography>
                    </TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.mobile}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>{row.message}</TableCell>
                    <TableCell align="right">
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={row.status || "new"}
                          onChange={(e) => handleStatusChange(row.id, e.target.value)}
                          displayEmpty
                          renderValue={(v) => (
                            <Chip
                              label={STATUS_OPTIONS.find((o) => o.value === v)?.label || v}
                              size="small"
                              color={getStatusColor(v)}
                            />
                          )}
                        >
                          {STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default Contacts;
